import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import fetchData from "../../functions/fetchData";
import "./supplierLedgerEntry.css";
import { Select } from 'antd';

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";
import { FaEdit, FaTrash, FaPlus, FaFileAlt, FaEye, FaCalculator } from "react-icons/fa";

const { Option } = Select;

export default function SupplierLedgerEntry() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [supplierBalance, setSupplierBalance] = useState(0);
    const [customerBalance, setCustomerBalance] = useState(0);
    const [editingItem, setEditingItem] = useState(null);
    const [supplierSearch, setSupplierSearch] = useState("");
    const [customerSearch, setCustomerSearch] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editModalData, setEditModalData] = useState({
        id: "",
        date: "",
        description: "",
        amount: "",
        reference_id: "",
        source_table: "",
        entry_category: ""
    });
    const [formData, setFormData] = useState({
        transaction_id: "",
        date: format(new Date(), "yyyy-MM-dd"),
        account_type: "Account Payable",
        supplier_id: "",
        customer_id: "",
        description: "",
        amount: "",
        entry_type: "credit", // 'credit' for supplier payments, 'debit' for customer receipts
        reference_id: "",
        receipt_type: "pending_bill", // for customer receipt: pending_bill, sponsor, advance, other
    });

    // Filters
    const [filters, setFilters] = useState({
        supplier_id: "",
        start_date: "",
        end_date: "",
        entry_type: ""
    });

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                console.log("Fetching suppliers...");
                fetchData("supplier", setSuppliers, "id", {});
            } catch (err) {
                console.error("Error fetching suppliers:", err);
                toast.error("Failed to load suppliers.");
                setSuppliers([]);
            }
        };
        
        const fetchCustomers = async () => {
            try {
                console.log("Fetching customers...");
                fetchData("customers", setCustomers, "id", {});
            } catch (err) {
                console.error("Error fetching customers:", err);
                toast.error("Failed to load customers.");
                setCustomers([]);
            }
        };
        
        console.log("Component mounted, fetching data...");
        fetchSuppliers();
        fetchCustomers();
        fetchLedgerEntries();
    }, []);

    const fetchLedgerEntries = async () => {
        try {
            console.log("Fetching ledger entries from both tables...");
            
            // Fetch supplier ledger entries
            const supplierEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("supplier_ledger_entries", (data) => {
                    data.forEach(entry => {
                        tempData.push({
                            ...entry,
                            source_table: 'supplier_ledger_entries',
                            entry_category: entry.credit_amount > 0 ? 'credit' : 'debit'
                        });
                    });
                    resolve(tempData);
                }, "id", {});
            });
            
            // Fetch customer ledger entries
            const customerEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("customer_ledger_entries", (data) => {
                    data.forEach(entry => {
                        tempData.push({
                            ...entry,
                            source_table: 'customer_ledger_entries',
                            entry_category: entry.debit_amount > 0 ? 'debit' : 'credit',
                            customer_id: entry.customer_id || entry.account_id
                        });
                    });
                    resolve(tempData);
                }, "id", {});
            });
            
            // Combine both datasets
            const combinedData = [...supplierEntries, ...customerEntries];
            
            // Sort by date (newest first)
            combinedData.sort((a, b) => new Date(b.date) - new Date(a.date));
            
            setData(combinedData);
            setFilteredData(combinedData);
            
            console.log("Combined ledger entries:", combinedData);
            
        } catch (error) {
            console.error("Error fetching ledger entries:", error);
            setData([]);
            setFilteredData([]);
            toast.error("Failed to fetch ledger entries. Please check your connection.");
        }
    };

    const calculateSupplierBalance = async (supplierId) => {
        try {
            console.log("Calculating balance for supplier:", supplierId);
            const response = await axios.get(`/supplier-ledger-balance/${supplierId}`, getHeaders());
            console.log("Balance response:", response.data);
            
            if (response.data.success) {
                setSupplierBalance(response.data.balance || 0);
            } else {
                console.error("Balance API returned success: false", response.data);
                setSupplierBalance(0);
            }
        } catch (error) {
            console.error("Error calculating balance:", error.response?.data || error.message);
            setSupplierBalance(0);
        }
    };

    const calculateCustomerBalance = async (customerId) => {
        try {
            console.log("Calculating balance for customer:", customerId);
            // You'll need to implement this endpoint on backend
            const response = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            console.log("Customer balance response:", response.data);
            
            if (response.data.success) {
                setCustomerBalance(response.data.balance || 0);
            } else {
                console.error("Customer balance API returned success: false", response.data);
                setCustomerBalance(0);
            }
        } catch (error) {
            console.error("Error calculating customer balance:", error.response?.data || error.message);
            setCustomerBalance(0);
        }
    };

    const handleSupplierChange = (supplierId) => {
        setFormData(prev => ({ ...prev, supplier_id: supplierId }));
        if (supplierId) {
            const supplier = suppliers.find(s => s.id === parseInt(supplierId));
            setSelectedSupplier(supplier);
            calculateSupplierBalance(supplierId);
        } else {
            setSelectedSupplier(null);
            setSupplierBalance(0);
        }
    };

    const handleCustomerChange = (customerId) => {
        setFormData(prev => ({ ...prev, customer_id: customerId }));
        if (customerId) {
            const customer = customers.find(c => c.id === parseInt(customerId));
            setSelectedCustomer(customer);
            calculateCustomerBalance(customerId);
        } else {
            setSelectedCustomer(null);
            setCustomerBalance(0);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "supplier_id") {
            handleSupplierChange(value);
        } else if (name === "customer_id") {
            handleCustomerChange(value);
        } else if (name === "entry_type") {
            // Reset selections when entry type changes
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                supplier_id: "",
                customer_id: ""
            }));
            setSelectedSupplier(null);
            setSelectedCustomer(null);
            setSupplierBalance(0);
            setCustomerBalance(0);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const transactionId = formData.transaction_id || `TXN_${Date.now()}`;
        const amount = parseFloat(formData.amount);

        try {
            if (formData.entry_type === 'credit') {
                // Credit entry: Payment to supplier -> save to supplier_ledger_entries
                const supplierLedgerData = {
                    transaction_id: transactionId,
                    date: formData.date,
                    account_type: "Account Payable",
                    supplier_id: parseInt(formData.supplier_id),
                    description: formData.description,
                    debit_amount: 0.00,
                    credit_amount: amount, // Credit to reduce supplier balance
                    reference_id: formData.reference_id || null,
                };
                
                const response = await axios.post("/supplier-ledger-entry", supplierLedgerData, getHeaders());
                
                if (response.data.success) {
                    toast.success("Supplier payment recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save supplier payment");
                }
                
            } else if (formData.entry_type === 'debit') {
                // Debit entry: Receipt from customer -> save to customer ledger
                const customerLedgerData = {
                    customer_id: parseInt(formData.customer_id),
                    description: formData.description,
                    debit_amount: amount, // Debit customer account
                    credit_amount: 0.00,
                    account_type: "Account Receivable",
                    reference_id: formData.reference_id || null,
                    date: formData.date,
                    receipt_type: formData.receipt_type,
                };
                
                const response = await axios.post("/customer-ledger-entry", customerLedgerData, getHeaders());
                
                if (response.data.success) {
                    toast.success("Customer receipt recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save customer receipt");
                }
            }

            resetForm();
            fetchLedgerEntries();
            
            // Update balance based on entry type
            if (formData.entry_type === 'credit' && formData.supplier_id) {
                calculateSupplierBalance(formData.supplier_id);
            } else if (formData.entry_type === 'debit' && formData.customer_id) {
                calculateCustomerBalance(formData.customer_id);
            }
            
        } catch (error) {
            console.error(error);
            toast.error("Failed to save entry.");
        }
    };

    const resetForm = () => {
        setFormData({
            transaction_id: "",
            date: format(new Date(), "yyyy-MM-dd"),
            account_type: "Account Payable",
            supplier_id: "",
            customer_id: "",
            description: "",
            amount: "",
            entry_type: "credit",
            reference_id: "",
            receipt_type: "pending_bill",
        });
        setEditingItem(null);
        setSelectedSupplier(null);
        setSelectedCustomer(null);
        setSupplierSearch("");
        setCustomerSearch("");
        setSupplierBalance(0);
        setCustomerBalance(0);
    };

    const handleEdit = (item) => {
        setEditModalData({
            id: item.id,
            date: format(new Date(item.date), "yyyy-MM-dd"),
            description: item.description,
            amount: item.debit_amount > 0 ? item.debit_amount : item.credit_amount,
            reference_id: item.reference_id || "",
            source_table: item.source_table,
            entry_category: item.entry_category,
            supplier_id: item.supplier_id,
            customer_id: item.customer_id
        });
        setShowEditModal(true);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setEditModalData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateEntry = async () => {
        try {
            const amount = parseFloat(editModalData.amount);
            
            if (editModalData.source_table === 'supplier_ledger_entries') {
                // Update supplier ledger entry
                const updateData = {
                    description: editModalData.description,
                    debit_amount: editModalData.entry_category === 'debit' ? amount : 0,
                    credit_amount: editModalData.entry_category === 'credit' ? amount : 0,
                    reference_id: editModalData.reference_id || null,
                    transaction_date: editModalData.date
                };

                const response = await axios.put(
                    `/supplier-ledger-entry/${editModalData.id}`,
                    updateData,
                    getHeaders()
                );

                if (response.data.success) {
                    toast.success("Supplier ledger entry updated successfully!");
                    setShowEditModal(false);
                    fetchLedgerEntries();
                } else {
                    throw new Error(response.data.message || "Failed to update entry");
                }
            } else if (editModalData.source_table === 'customer_ledger_entries') {
                // Update customer ledger entry
                const updateData = {
                    description: editModalData.description,
                    debit_amount: editModalData.entry_category === 'debit' ? amount : 0,
                    credit_amount: editModalData.entry_category === 'credit' ? amount : 0,
                    reference_id: editModalData.reference_id || null,
                    transaction_date: editModalData.date
                };

                const response = await axios.put(
                    `/customer-ledger-entry/${editModalData.id}`,
                    updateData,
                    getHeaders()
                );

                if (response.data.success) {
                    toast.success("Customer ledger entry updated successfully!");
                    setShowEditModal(false);
                    fetchLedgerEntries();
                } else {
                    throw new Error(response.data.message || "Failed to update entry");
                }
            }
        } catch (error) {
            console.error("Error updating entry:", error);
            toast.error(error.response?.data?.message || "Failed to update entry");
        }
    };

    const handleDelete = async (id, sourceTable) => {
        if (window.confirm("Are you sure you want to delete this entry?")) {
            try {
                await axios.delete(`/deletebyid/${sourceTable}/id/${id}`, getHeaders());
                toast.success("Entry deleted successfully!");
                fetchLedgerEntries();
            } catch (error) {
                console.error(error);
                toast.error(error.response?.data?.message || "Failed to delete entry.");
            }
        }
    };

    const applyFilters = () => {
        let filtered = data;

        if (filters.supplier_id) {
            filtered = filtered.filter(item => 
                (item.source_table === 'supplier_ledger_entries' && item.supplier_id === parseInt(filters.supplier_id)) ||
                (item.source_table === 'customer_ledger_entries' && item.customer_id === parseInt(filters.supplier_id))
            );
        }
        
        if (filters.start_date) {
            const startDate = new Date(filters.start_date);
            startDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.date);
                itemDate.setHours(0, 0, 0, 0);
                return itemDate >= startDate;
            });
        }
        
        if (filters.end_date) {
            const endDate = new Date(filters.end_date);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.date);
                itemDate.setHours(0, 0, 0, 0);
                return itemDate <= endDate;
            });
        }
        
        if (filters.entry_type) {
            filtered = filtered.filter(item => {
                if (filters.entry_type === 'debit') {
                    // Show expenses (supplier ledger debit entries)
                    return item.entry_category === 'debit' && item.source_table === 'supplier_ledger_entries';
                } else if (filters.entry_type === 'credit') {
                    // Show supplier payments (supplier ledger credit entries)
                    return item.entry_category === 'credit' && item.source_table === 'supplier_ledger_entries';
                } else if (filters.entry_type === 'customer_receipt') {
                    // Show customer receipts (customer ledger entries)
                    return item.source_table === 'customer_ledger_entries';
                }
                return true;
            });
        }

        setFilteredData(filtered);
    };

    const resetFilters = () => {
        setFilters({
            supplier_id: "",
            start_date: "",
            end_date: "",
            entry_type: ""
        });
        setFilteredData(data);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' THB';
    };

    return (
        <Layout>
            <Header title="Supplier Ledger Entry" />
            <ToastContainer />
            
            <div className="supplier-ledger-container">
            {/* Compact Entry Form */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="supplier-entry-form-card">
                        <div className="supplier-entry-form-header">
                            <h6 className="supplier-entry-form-title">
                                <FaPlus className="me-2" />
                                {editingItem ? 'Edit Entry' : 'New Entry'}
                            </h6>
                            {(selectedSupplier || selectedCustomer) && (
                                <div className="supplier-balance-display">
                                    <FaCalculator />
                                    <span>
                                        {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'} Balance: 
                                        <span className="supplier-balance-amount">
                                            {formatCurrency(formData.entry_type === 'credit' ? supplierBalance : customerBalance)}
                                        </span>
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="supplier-entry-form-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-2">
                                    <div className="col-md-2">
                                        <label className="form-label small">Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Entry Type</label>
                                        <select
                                            className="form-control form-control-sm"
                                            name="entry_type"
                                            value={formData.entry_type}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="credit">Supplier Payment</option>
                                            <option value="debit">Customer Receipt</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">
                                            {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'}
                                        </label>
                                        {formData.entry_type === 'credit' ? (
                                            <Select
                                                showSearch
                                                allowClear
                                                style={{ width: '100%' }}
                                                placeholder="Select supplier"
                                                value={supplierSearch || undefined}
                                                onChange={(value) => {
                                                    if (value) {
                                                        setSupplierSearch(value);
                                                        const selectedSupplier = suppliers.find(s => s.name === value);
                                                        if (selectedSupplier) {
                                                            handleInputChange({
                                                                target: {
                                                                    name: 'supplier_id',
                                                                    value: selectedSupplier.id
                                                                }
                                                            });
                                                        }
                                                    } else {
                                                        setSupplierSearch('');
                                                        handleInputChange({
                                                            target: {
                                                                name: 'supplier_id',
                                                                value: ''
                                                            }
                                                        });
                                                    }
                                                }}
                                                filterOption={(input, option) =>
                                                    option?.children && typeof option.children === 'string'
                                                        ? option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                        : false
                                                }
                                            >
                                                {suppliers.map((supplier) => (
                                                    <Option key={supplier.id} value={supplier.name}>{supplier.name}</Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Select
                                                showSearch
                                                allowClear
                                                style={{ width: '100%' }}
                                                placeholder="Select customer"
                                                value={customerSearch || undefined}
                                                onChange={(value) => {
                                                    if (value) {
                                                        setCustomerSearch(value);
                                                        const selectedCustomer = customers.find(c => c.name === value);
                                                        if (selectedCustomer) {
                                                            handleInputChange({
                                                                target: {
                                                                    name: 'customer_id',
                                                                    value: selectedCustomer.id
                                                                }
                                                            });
                                                        }
                                                    } else {
                                                        setCustomerSearch('');
                                                        handleInputChange({
                                                            target: {
                                                                name: 'customer_id',
                                                                value: ''
                                                            }
                                                        });
                                                    }
                                                }}
                                                filterOption={(input, option) =>
                                                    option?.children && typeof option.children === 'string'
                                                        ? option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                        : false
                                                }
                                            >
                                                {customers.map((customer) => (
                                                    <Option key={customer.id} value={customer.name}>{customer.name}</Option>
                                                ))}
                                            </Select>
                                        )}
                                    </div>
                                    {formData.entry_type === 'debit' && (
                                        <div className="col-md-2">
                                            <label className="form-label small">Receipt Type</label>
                                            <select
                                                className="form-control form-control-sm"
                                                name="receipt_type"
                                                value={formData.receipt_type}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="pending_bill">Pending Bill</option>
                                                <option value="sponsor">Sponsor</option>
                                                <option value="advance">Advance</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className={formData.entry_type === 'debit' ? 'col-md-4' : 'col-md-4'}>
                                        <label className="form-label small">Description</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Transaction description"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small">Amount</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control form-control-sm"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Reference ID</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            name="reference_id"
                                            value={formData.reference_id}
                                            onChange={handleInputChange}
                                            placeholder="Optional reference"
                                        />
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <div className="w-100">
                                            <label className="supplier-form-label invisible">.</label>
                                            <div  style={{ marginTop: '10px' }}>
                                                <button type="submit" className="supplier-btn-primary supplier-btn-sm me-2">
                                                    {editingItem ? 'Update' : 'Save'} {formData.entry_type === 'credit' ? 'Payment' : 'Receipt'}
                                                </button>
                                                {editingItem && (
                                                    <button 
                                                        type="button" 
                                                        className="supplier-btn-secondary supplier-btn-sm"
                                                        onClick={resetForm}
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="supplier-filter-card">
                        <div className="supplier-filter-header">
                            <h6 className="supplier-filter-title">
                                <FaFileAlt />
                                Filters & Reports
                            </h6>
                        </div>
                        <div className="supplier-filter-body">
                            <div className="row g-2">
                                <div className="col-md-3">
                                    <label className="form-label small">Filter by Supplier/Customer</label>
                                    <Select
                                        showSearch
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="All Suppliers & Customers"
                                        value={filters.supplier_id || undefined}
                                        onChange={(value) => setFilters(prev => ({...prev, supplier_id: value || ""}))}
                                        filterOption={(input, option) =>
                                            option?.children && typeof option.children === 'string'
                                                ? option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                : false
                                        }
                                    >
                                        <Select.OptGroup label="Suppliers">
                                            {suppliers.map((supplier) => (
                                                <Option key={`sup_${supplier.id}`} value={supplier.id}>
                                                    {supplier.name}
                                                </Option>
                                            ))}
                                        </Select.OptGroup>
                                        <Select.OptGroup label="Customers">
                                            {customers.map((customer) => (
                                                <Option key={`cust_${customer.id}`} value={customer.id}>
                                                    {customer.name}
                                                </Option>
                                            ))}
                                        </Select.OptGroup>
                                    </Select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filters.start_date}
                                        onChange={(e) => setFilters(prev => ({...prev, start_date: e.target.value}))}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filters.end_date}
                                        onChange={(e) => setFilters(prev => ({...prev, end_date: e.target.value}))}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">Entry Type</label>
                                    <select
                                        className="form-control form-control-sm"
                                        value={filters.entry_type}
                                        onChange={(e) => setFilters(prev => ({...prev, entry_type: e.target.value}))}
                                    >
                                        <option value="">All Types</option>
                                        <option value="debit">Expenses</option>
                                        <option value="credit">Supplier Payments</option>
                                        <option value="customer_receipt">Customer Receipts</option>
                                    </select>
                                </div>
                                <div className="col-md-3 d-flex align-items-end">
                                    <div style={{ marginTop: '40px' }}>
                                        <button 
                                            className="supplier-btn-primary supplier-btn-sm me-2"
                                            onClick={applyFilters}
                                        >
                                            Apply Filters
                                        </button>
                                        <button 
                                            className="supplier-btn-secondary supplier-btn-sm"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Reports Table */}
            <div className="row">
                <div className="col-12">
                    <div className="supplier-ledger-table-card">
                        <div className="supplier-ledger-table-header">
                            <h6 className="supplier-ledger-table-title">
                                <FaEye />
                                Ledger Entries ({filteredData.length} records)
                            </h6>
                        </div>
                        <div className="supplier-ledger-table-body">
                            {filteredData.length === 0 ? (
                                <div className="supplier-empty-state">
                                    <p className="supplier-empty-text">No ledger entries found.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="supplier-ledger-table">
                                        <thead className="table-dark">
                                            <tr>
                                                <th className="py-2 px-3">Date</th>
                                                <th className="py-2 px-3">Transaction ID</th>
                                                <th className="py-2 px-3">Supplier/Customer</th>
                                                <th className="py-2 px-3">Description</th>
                                                <th className="py-2 px-3">Type</th>
                                                <th className="py-2 px-3 text-end">Debit</th>
                                                <th className="py-2 px-3 text-end">Credit</th>
                                                <th className="py-2 px-3 text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((entry) => {
                                                // Find supplier or customer based on entry source
                                                const supplier = entry.source_table === 'supplier_ledger_entries' 
                                                    ? suppliers.find(s => s.id === entry.supplier_id)
                                                    : null;
                                                const customer = entry.source_table === 'customer_ledger_entries'
                                                    ? customers.find(c => c.id === entry.customer_id)
                                                    : null;
                                                
                                                return (
                                                    <tr key={`${entry.source_table}_${entry.id}`}>
                                                        <td className="py-2 px-3 small">
                                                            {format(new Date(entry.date), "MMM dd, yyyy")}
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            <span className="supplier-badge-secondary">
                                                                {entry.transaction_id}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            <strong>
                                                                {supplier ? supplier.name : 
                                                                 customer ? customer.name :
                                                                 'Unknown'}
                                                            </strong>
                                                            <br />
                                                            <small className="supplier-text-muted">
                                                                {entry.source_table === 'supplier_ledger_entries' ? 'Supplier' : 'Customer'}
                                                            </small>
                                                        </td>
                                                        <td className="py-2 px-3 small">
                                                            {entry.description}
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <span className={entry.entry_category === 'debit' ? 'supplier-badge-info' : 'supplier-badge-success'}>
                                                                {entry.entry_category === 'debit' 
                                                                    ? (entry.source_table === 'supplier_ledger_entries' ? 'Supplier Expense' : 'Customer Receipt')
                                                                    : 'Supplier Payment'
                                                                }
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 supplier-text-end small">
                                                            {entry.debit_amount > 0 && (
                                                                <span className="supplier-text-danger supplier-fw-bold">
                                                                    {formatCurrency(entry.debit_amount)}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3 supplier-text-end small">
                                                            {entry.credit_amount > 0 && (
                                                                <span className="supplier-text-success supplier-fw-bold">
                                                                    {formatCurrency(entry.credit_amount)}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3 supplier-text-center">
                                                            <button
                                                                className="supplier-btn-outline-primary supplier-btn-sm me-1"
                                                                onClick={() => handleEdit(entry)}
                                                                title="Edit Entry"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                className="supplier-btn-outline-danger supplier-btn-sm"
                                                                onClick={() => handleDelete(entry.id, entry.source_table)}
                                                                title="Delete Entry"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="5" className="py-2 px-3 supplier-text-end supplier-fw-bold">Totals:</td>
                                                <td className="py-2 px-3 supplier-text-end supplier-fw-bold supplier-text-danger">
                                                    {formatCurrency(filteredData.reduce((sum, entry) => sum + parseFloat(entry.debit_amount || 0), 0))}
                                                </td>
                                                <td className="py-2 px-3 supplier-text-end supplier-fw-bold supplier-text-success">
                                                    {formatCurrency(filteredData.reduce((sum, entry) => sum + parseFloat(entry.credit_amount || 0), 0))}
                                                </td>
                                                <td className="py-2 px-3"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowEditModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FaEdit className="me-2" />
                                    Edit Ledger Entry
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label small">Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        name="date"
                                        value={editModalData.date}
                                        onChange={handleModalChange}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small">Description</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        name="description"
                                        value={editModalData.description}
                                        onChange={handleModalChange}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small">Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        name="amount"
                                        value={editModalData.amount}
                                        onChange={handleModalChange}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small">Reference ID</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        name="reference_id"
                                        value={editModalData.reference_id}
                                        onChange={handleModalChange}
                                    />
                                </div>
                                <div className="alert alert-info small">
                                    <strong>Entry Type:</strong> {editModalData.entry_category === 'debit' 
                                        ? (editModalData.source_table === 'supplier_ledger_entries' ? 'Supplier Expense' : 'Customer Receipt')
                                        : 'Supplier Payment'}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleUpdateEntry}>
                                    Update Entry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </Layout>
    );
}
